/**
 * Scatter Plot Chart Type Definition
 */

import {
  paletteControl,
  gridControl,
  titleControl,
  xLabelControl,
  yLabelControl,
  xMinControl,
  xMaxControl,
  yMinControl,
  yMaxControl,
  valueDecimalsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  xAxisRangeCode,
  yAxisRangeCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';
import { SERIES_MARKERS_MPL } from '../../utils/accessibility';
import { getColors } from '../shared/palettes';

const scatterPlot = {
  id: 'scatter-plot',
  name: 'Scatter Plot',
  category: 'scatter',
  description: 'Visualize relationships between two variables',
  
  sampleData: [
    // Group A
    { x: 12, y: 10, category: 'Group A', label: 'Point 1' },
    { x: 18, y: 11, category: 'Group A', label: 'Point 2' },
    { x: 22, y: 1, category: 'Group A', label: 'Point 3' },
    { x: 35, y: 20, category: 'Group A', label: 'Point 4' },
    { x: 45, y: 25, category: 'Group A', label: '' },
    { x: 48, y: 18, category: 'Group A', label: '' },
    // Group B
    { x: 25, y: 22, category: 'Group B', label: 'Point 5' },
    { x: 30, y: 17, category: 'Group B', label: 'Point 6' },
    { x: 32, y: 17, category: 'Group B', label: '' },
    { x: 40, y: 50, category: 'Group B', label: 'Point 7' },
    { x: 33, y: 19, category: 'Group B', label: '' },
    // Group C
    { x: 21, y: 17, category: 'Group C', label: 'Point 8' },
    { x: 28, y: 2, category: 'Group C', label: 'Point 9' },
    { x: 34, y: 6, category: 'Group C', label: 'Point 10' },
    { x: 15, y: 4, category: 'Group C', label: 'Point 11' },
    { x: 47, y: 18, category: 'Group C', label: '' },
  ],
  
  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    showGrid: true,
    markerSize: 100,
    markerAlpha: 1.0,
    useSeriesStyles: false,
    showLegend: true,
    legendPosition: 'top',
    scatterLabels: 'off',
    valueDecimals: 1,
    xlabel: 'X',
    ylabel: 'Y',
    title: '',
    xMin: '',
    xMax: '',
    yMin: '',
    yMax: '',
  },
  
  controls: [
    paletteControl,
    {
      key: 'markerSize',
      label: 'Marker Size',
      type: 'slider',
      min: 20,
      max: 300,
      step: 20,
      unit: 'px',
    },
    {
      key: 'markerAlpha',
      label: 'Marker Transparency',
      type: 'slider',
      min: 0.1,
      max: 1.0,
      step: 0.1,
      unit: '',
    },
    gridControl,
    {
      key: 'scatterLabels',
      label: 'Point Labels',
      type: 'select',
      options: [
        { label: 'Off', value: 'off' },
        { label: 'Point Name', value: 'name' },
        { label: 'X Value', value: 'x' },
        { label: 'Y Value', value: 'y' },
        { label: 'X & Y Values', value: 'xy' },
      ],
    },
    valueDecimalsControl,
    {
      key: 'useSeriesStyles',
      label: 'Use Non-Color Series Styles',
      type: 'toggle',
    },
    {
      key: 'showLegend',
      label: 'Show Legend',
      type: 'toggle',
    },
    {
      key: 'legendPosition',
      label: 'Legend Position',
      type: 'select',
      options: [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Right', value: 'right' },
      ],
    },
    xLabelControl,
    yLabelControl,
    titleControl,
    xMinControl,
    xMaxControl,
    yMinControl,
    yMaxControl,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.x && dataContext.mapping?.y;
    const colors = JSON.stringify(getColors(config, 5, s));

    const markerStyles = JSON.stringify(SERIES_MARKERS_MPL);
    const useSeriesStyles = !!config.useSeriesStyles;
    const sl = config.scatterLabels || 'off';
    const d = config.valueDecimals ?? 1;

    const labelFmt = sl === 'name' ? 'str(category)'
      : sl === 'x' ? `f'{xi:.${d}f}'`
      : sl === 'y' ? `f'{yi:.${d}f}'`
      : sl === 'xy' ? `f'({xi:.${d}f}, {yi:.${d}f})'`
      : null;

    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? (dataContext.mapping.category
  ? `df = pd.read_csv('${dataContext.fileName}')
# Group by category
categories = {}
for name, group in df.groupby('${dataContext.mapping.category}'):
    categories[str(name)] = {'x': group['${dataContext.mapping.x}'].tolist(), 'y': group['${dataContext.mapping.y}'].tolist()}`
  : `df = pd.read_csv('${dataContext.fileName}')
categories = {'Data': {'x': df['${dataContext.mapping.x}'].tolist(), 'y': df['${dataContext.mapping.y}'].tolist()}}`)
: `categories = {
    'Transformers': {
        'x': [12, 18, 22, 35, 45, 48],  # X values for category 1
        'y': [10, 11, 1, 20, 25, 18]  # Y values for category 1
    },
    'CNNs': {
        'x': [25, 30, 32, 40, 33],  # X values for category 2
        'y': [22, 17, 17, 50, 19]  # Y values for category 2
    },
    'Diffusion Models': {
        'x': [21, 28, 34, 15, 47],  # X values for category 3
        'y': [17, 2, 6, 4, 18]  # Y values for category 3
    }
}`}
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Define colors for each model category
colors = ${colors}
marker_styles = ${markerStyles}

# Create scatter plot for each category
for idx, (category, data) in enumerate(categories.items()):
    marker_style = marker_styles[idx % len(marker_styles)] if ${useSeriesStyles ? 'True' : 'False'} else 'o'
    ax.scatter(data['x'], data['y'],
              s=${config.markerSize || 100},
              c=colors[idx % len(colors)],
              alpha=${config.markerAlpha || 0.7},
              marker=marker_style,
              edgecolors='${s?.axisColor || '#000000'}',
              linewidths=1.0,
              label=category,
              zorder=3)
${labelFmt ? `
# Point labels
for category, data in categories.items():
    for xi, yi in zip(data['x'], data['y']):
        ax.annotate(${labelFmt}, (xi, yi), textcoords='offset points',
                   xytext=(5, 5), fontsize=8, color='${s?.foreground || '#000000'}', zorder=4)` : '# Point labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Add grid
${gridCode(config, 'both', s)}

${config.showLegend ? `# Add legend
ax.legend(loc='${config.legendPosition === 'top' ? 'upper center' : config.legendPosition === 'bottom' ? 'lower center' : 'center left'}',
         bbox_to_anchor=(${config.legendPosition === 'top' ? '0.5, 1.12' : config.legendPosition === 'bottom' ? '0.5, -0.15' : '1.05, 0.5'}),
         frameon=False, fontsize=${s?.legendSize || 10}, ncol=${config.legendPosition === 'right' ? '1' : '3'})` : '# Legend disabled'}

# Set axis ranges
${xAxisRangeCode(config)}
${yAxisRangeCode(config)}
${spineCode(s)}
${finishCode('scatter_plot')}`;
  },
};

export default scatterPlot;
